import { IonContent, IonFab, IonFabButton, IonIcon, IonPage } from '@ionic/react';
import { IconProps, MagnifyingGlassIcon, ReceiptIcon } from '@phosphor-icons/react';
import React from 'react';

import { add } from 'ionicons/icons';
import { Redirect } from 'react-router';

import ReintegroCard from '../../components/Reintegro/ReintegroCard';
import VacunaReintegroCard from '../../components/Reintegro/VacunaReintegroCard';
import SiniestroCard from '../../components/Siniestro/SiniestroCard';
import Alert from '../../components/UI/Alert';
import Gallery from '../../components/UI/Gallery';
import Modal from '../../components/UI/Modal';
import Navigation from '../../components/UI/Navigation';
import PageRefresher from '../../components/UI/PageRefresher';
import Sorter from '../../components/UI/Sorter';
import Spinner from '../../components/UI/Spinner';
import Toast from '../../components/UI/Toast';
import { useSiniestroPage } from '../../hooks/Helpers/useSiniestroPage';
import { reintegros_sortCriteria } from '../../utils/reintegro';
import './SiniestroPage.scss';

export const iconSiniestroProps: IconProps = { weight: 'fill' };

const SiniestroPage: React.FC = () => {
  const {
    currentPet,
    currentSiniestro,
    SVFs,
    comprobantesBySVFs,
    isComprobantesValidating,
    vacunaComprobantes,
    isVacunaComprobantesLoading,
    denuncia_administrativa,
    fecha_carga,
    documentacionCompleta,
    isVacuna,
    hasReintegros,
    hasCompletedSVF,
    documentacionExtraCount,
    isDocumentacionExtraLoading,

    openVacunaComprobantesGallery,
    openComprobanteVacuna,

    onResendVeterinario,
    resendingVetFormId,
    openWizard,
    openVeterinario,
    openDocumentacion,
    openComprobante,
    openDocumentacionExtra,
    openViewDocumentacionExtra,

    isOpen,
    content,
    modalProps,
    closeModal,

    alertState,
    hideAlert,

    galleryItems,
    galleryTitle,
    isGalleryOpen,
    openComprobantesGallery,
    closeGallery,

    onRefresh,
    isInitialLoading,

    toastState,
    hideToast,
  } = useSiniestroPage();

  // Si la mascota cambió y ya no hay siniestro seleccionado, volver al listado.
  // Usamos Redirect declarativo (como withPetGuard) para evitar navegación
  // imperativa durante transiciones de Ionic, que corrompería el outlet.
  if (!currentSiniestro) return <Redirect to="/tabs/pet" />;

  if (isInitialLoading) return <Spinner fullscreen />;

  return (
    <IonPage className="siniestro-page">
      <Navigation title={`Reintegro #${currentSiniestro?.id}`} />

      <IonContent className="ion-padding">
        <PageRefresher callback={onRefresh} />

        <SiniestroCard
          currentPet={currentPet}
          denuncia_administrativa={denuncia_administrativa}
          isVacuna={isVacuna}
          fecha_carga={fecha_carga}
          documentacionCompleta={documentacionCompleta}
          openDocViewer={openDocumentacion}
          hasCompletedSVF={hasCompletedSVF}
          documentacionExtraCount={documentacionExtraCount}
          isDocumentacionExtraLoading={isDocumentacionExtraLoading}
          onAddDocumentacionExtra={openDocumentacionExtra}
          onViewDocumentacionExtra={openViewDocumentacionExtra}
        />

        {isVacuna ? (
          <>
            <div className="siniestro-page__section-title">Mi Reintegro</div>
            <VacunaReintegroCard
              comprobantes={vacunaComprobantes}
              documentacionCompleta={documentacionCompleta}
              isLoadingComprobantes={isVacunaComprobantesLoading}
              onEditDocumentacion={openDocumentacion}
              onAddComprobante={openComprobanteVacuna}
              openComprobantesGallery={openVacunaComprobantesGallery}
            />
          </>
        ) : hasReintegros ? (
          <>
            <div className="siniestro-page__section-title">Mis Reintegros</div>
            <Sorter
              items={SVFs!}
              criteria={reintegros_sortCriteria}
              label="Mostrar"
              storageKey="reintegros_sort"
              hideEmptyCriteria
            >
              {(sortedSVFs) =>
                sortedSVFs.map((svf) => (
                  <ReintegroCard
                    key={svf.id}
                    svf={svf}
                    isVacuna={isVacuna}
                    documentacionCompleta={documentacionCompleta}
                    comprobantes={comprobantesBySVFs?.[svf.id]}
                    isLoadingComprobantes={isComprobantesValidating}
                    onEditVeterinario={openVeterinario}
                    onEditDocumentacion={openDocumentacion}
                    onAddComprobante={openComprobante}
                    openComprobantesGallery={openComprobantesGallery}
                    onResendVeterinario={onResendVeterinario}
                    resendingVetFormId={resendingVetFormId}
                  />
                ))
              }
            </Sorter>
          </>
        ) : (
          <div className="siniestro-page__empty-state">
            <div className="siniestro-page__empty-icon-wrapper">
              <ReceiptIcon size={80} weight="light" color="#d1d5db" />
              <div className="siniestro-page__empty-badge">
                <MagnifyingGlassIcon size={16} weight="bold" />
              </div>
            </div>
            <h3 className="siniestro-page__empty-title">Todavía no hay reintegros</h3>
            <p className="siniestro-page__empty-description">
              Para iniciar una solicitud de reembolso, tocá el botón + en la esquina
              inferior derecha.
            </p>
          </div>
        )}

        <IonFab vertical="bottom" horizontal="end" slot="fixed" onClick={openWizard}>
          <IonFabButton color="primary">
            <IonIcon icon={add} size="large" />
          </IonFabButton>
        </IonFab>
      </IonContent>

      <Modal isOpen={isOpen} onClose={closeModal} {...modalProps}>
        {content}
      </Modal>

      <Gallery
        isOpen={isGalleryOpen}
        onClose={closeGallery}
        files={galleryItems ?? []}
        title={galleryTitle}
        isValidating={false}
      />

      <Alert
        isOpen={alertState.isOpen}
        header={alertState.header}
        message={alertState.message}
        buttons={alertState.buttons}
        onDidDismiss={hideAlert}
      />

      <Toast
        isOpen={toastState.isOpen}
        message={toastState.message}
        color={toastState.color}
        duration={toastState.duration}
        onDidDismiss={hideToast}
      />
    </IonPage>
  );
};

export default SiniestroPage;
